import { composeThemeFromProps, Compose } from '@css-modules-theme/react';
import clsx from 'clsx';
import React, { forwardRef, useRef } from 'react';
import mergeRefs from 'react-merge-refs';
import { Count } from './Count.js';
import defaultStyles from './styles/Badge.module.scss.js';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { isFunction } from '../../utils/is.js';

const Badge = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    variant = 'text',
    count = 0,
    maxCount = 99,
    text,
    theme,
    style,
    status = 'success',
    isRounded = false,
    children,
    dataTestId,
    animateCount = false,
    ...restProps
  } = props;
  const styles = composeThemeFromProps(defaultStyles, {
    theme
  }, {
    compose: Compose.Merge
  });
  const badgeRef = useRef(null);
  const Component = 'div';
  const testProps = {
    'data-test-id': dataTestId
  };
  const roundedClasses = clsx({
    [styles.rounded]: isRounded === true
  });
  const statusClasses = clsx({
    [styles.pending]: status === 'pending',
    [styles.success]: status === 'success',
    [styles.cancelled]: status === 'cancelled',
    [styles.warning]: status === 'warning',
    [styles.critical]: status === 'critical',
    [styles.new]: status === 'new',
    [styles.bestseller]: status === 'bestseller'
  });
  const getDom = () => {
    switch (variant) {
      case 'count':
        return count ? /*#__PURE__*/jsx(Count, {
          className: clsx(styles.number, statusClasses, roundedClasses),
          style: style,
          maxCount: maxCount,
          count: count,
          animateCount: animateCount
        }) : null;
      case 'text':
        return text ? /*#__PURE__*/jsx("span", {
          className: clsx(styles.text, statusClasses, roundedClasses),
          style: style,
          children: text
        }) : null;
      case 'inlineDot':
        return /*#__PURE__*/jsxs("span", {
          className: styles.inline_dot,
          children: [/*#__PURE__*/jsx("span", {
            style: style,
            className: clsx(styles.dot, statusClasses, roundedClasses)
          }), text && /*#__PURE__*/jsx("span", {
            className: styles.label,
            children: text
          })]
        });
      default:
        return null;
    }
  };

  // Added support for custom rendering
  if (isFunction(children)) {
    return children({
      ...props,
      statusClasses
    });
  }
  return React.Children.count(children) ? /*#__PURE__*/jsxs(Component, {
    className: styles.root,
    ref: mergeRefs([badgeRef, ref]),
    ...restProps,
    ...testProps,
    children: [children, getDom()]
  }) : /*#__PURE__*/jsx(Fragment, {
    children: getDom()
  });
});
Badge.displayName = 'Badge';

export { Badge };
